import React from 'react';
import { useFeatureFlagPayload, usePostHog } from 'posthog-js/react';
import { toast } from 'react-toastify';

import { useGetConsultationTranscriptionQuery } from '../../services/consultation-results.api';
import { useGetConsultationQuery } from '../../services/users.api';
import { useGetTranscriptionsQuery } from '../../services/consultation-audio.api';

import { Button } from '../../shared/ui';

import { AiAssistantDisclaimer } from './ai-assistant-disclaimer';
import { AiAssistantMissingMessage } from './ai-assistant-missing-message';

type Props = {
  consultationId?: number;
  isGroupConsultation?: boolean;
  /** Запрашивать транскрипцию без проверки feature flag (вкладка в SessionDetail). */
  bypassFeatureFlag?: boolean;
};

type TranscriptChunk = {
  speaker: '0' | '1' | '2';
  text: string;
};

type TranscriptionFeaturePayload = {
  allow?: boolean;
  expiration?: number;
};

const CopySmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 9.75a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 21 9.75v9a2.25 2.25 0 0 1-2.25 2.25h-7.5A2.25 2.25 0 0 1 9 18.75v-9Z"
      stroke="#202020"
      strokeWidth="1.5"
    />
    <path
      d="M15 7.5V5.25A2.25 2.25 0 0 0 12.75 3h-7.5A2.25 2.25 0 0 0 3 5.25v9a2.25 2.25 0 0 0 2.25 2.25H9"
      stroke="#202020"
      strokeWidth="1.5"
    />
  </svg>
);

const PrintSmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9V4.5h12V9" stroke="#202020" strokeWidth="1.5" />
    <path
      d="M6.75 15h10.5a2.25 2.25 0 0 0 2.25-2.25v-2.5A2.25 2.25 0 0 0 17.25 8H6.75A2.25 2.25 0 0 0 4.5 10.25v2.5A2.25 2.25 0 0 0 6.75 15Z"
      stroke="#202020"
      strokeWidth="1.5"
    />
    <path d="M6 13.5h12V20H6v-6.5Z" stroke="#202020" strokeWidth="1.5" />
    <circle cx="17.25" cy="10.75" r="0.75" fill="#202020" />
  </svg>
);

const speakerOrder: Array<'0' | '1' | '2'> = ['0', '1', '2'];

const parseTranscript = (value: string): TranscriptChunk[] => {
  const normalized = value.replace(/\r\n/g, '\n');
  const matches = Array.from(normalized.matchAll(/(^|\n)\s*(?:\*\*)?Speaker\s*(0|1|2)\s*:\s*(?:\*\*)?\s*/g));

  if (!matches.length) {
    return [];
  }

  return matches
    .map((match, idx) => {
      const speaker = match[2] as '0' | '1' | '2';
      const markerStart = (match.index ?? 0) + match[0].length;
      const nextMarkerIndex =
        idx + 1 < matches.length ? (matches[idx + 1].index ?? normalized.length) : normalized.length;
      const text = normalized.slice(markerStart, nextMarkerIndex).trim();

      return {
        speaker,
        text,
      };
    })
    .filter((chunk) => chunk.text.length > 0);
};

export const AiTranscriptionTab = ({
  consultationId,
  isGroupConsultation = false,
  bypassFeatureFlag = false,
}: Props) => {
  const hasConsultationId = Boolean(consultationId);
  const posthog = usePostHog();
  const transcriptionFeaturePayload = useFeatureFlagPayload('allow-transcription');
  const resolvedPayload = transcriptionFeaturePayload;
  let parsedPayload: TranscriptionFeaturePayload = {};
  if (typeof resolvedPayload === 'string') {
    try {
      parsedPayload = JSON.parse(resolvedPayload) as TranscriptionFeaturePayload;
    } catch {
      parsedPayload = {};
    }
  } else if (resolvedPayload && typeof resolvedPayload === 'object') {
    parsedPayload = resolvedPayload as TranscriptionFeaturePayload;
  }

  const { data: consultation, isLoading: isConsultationLoading } = useGetConsultationQuery(
    consultationId?.toString() ?? '',
    {
      skip: !hasConsultationId,
    },
  );
  const isTranscriptionEnabled = bypassFeatureFlag || Boolean(parsedPayload.allow);
  const expirationHours = parsedPayload.expiration ?? 24;
  const expirationMs = Number(expirationHours) * 60 * 60 * 1000;
  const expirationHoursText = `${expirationHours}`.replace('.', ',');
  const isOfflineConsultation = consultation?.consultation_type === 'offline';
  const { data: offlineTranscriptions, isLoading: isOfflineTranscriptionsLoading } = useGetTranscriptionsQuery(
    { consultationId: consultationId?.toString() ?? '', limit: 1 },
    { skip: !hasConsultationId || !isOfflineConsultation },
  );
  const offlineExpiresAt = offlineTranscriptions?.items[0]?.expiresAt;
  const retentionHoursText = isOfflineConsultation ? '48' : expirationHoursText;
  const consultationEndTime = consultation?.datetime_end ? new Date(consultation.datetime_end).getTime() : null;
  const isConsultationExpired = isOfflineConsultation
    ? Boolean(offlineExpiresAt && Date.now() > new Date(offlineExpiresAt).getTime())
    : consultationEndTime !== null &&
      !Number.isNaN(consultationEndTime) &&
      Date.now() - consultationEndTime > expirationMs;

  const { data, isFetching, isLoading, isError } = useGetConsultationTranscriptionQuery(
    consultationId ? consultationId.toString() : '',
    {
      skip:
        !hasConsultationId ||
        !isTranscriptionEnabled ||
        isConsultationLoading ||
        isOfflineTranscriptionsLoading ||
        isConsultationExpired,
    },
  );

  const renderCard = (children: React.ReactNode, variant: 'default' | 'error' = 'default') => {
    const baseClasses = 'p-1 lg:p-1 text-body-l lg:text-body-xl leading-relaxed space-y-4';
    const variantClasses = variant === 'error' ? 'text-primary' : 'text-ink-muted';

    return <div className={`${baseClasses} ${variantClasses}`}>{children}</div>;
  };

  if (!hasConsultationId) {
    return renderCard(<>Выберите запись, чтобы увидеть транскрипцию.</>);
  }

  if (isConsultationLoading || isLoading || isFetching) {
    return renderCard(<p>Загружаем транскрипцию...</p>);
  }

  if (!isTranscriptionEnabled) {
    return renderCard(
      <div className="space-y-2 text-ink">
        <p className="text-heading-md">Транскрипция недоступна.</p>
      </div>,
      'error',
    );
  }

  if (isConsultationExpired) {
    return renderCard(
      <div className="space-y-2 text-ink">
        <p className="text-heading-md">Срок хранения транскрипта истек.</p>
        <p>
          В ZiNGiZi мы удаляем расшифровки через {retentionHoursText} ч. Это позволяет нам оптимизировать расходы на
          серверы и поддерживать выгодные тарифы на подписку.
        </p>
      </div>,
    );
  }

  if (isError || !data?.transcription) {
    return renderCard(
      <AiAssistantMissingMessage assistant="ai-transcription" isGroupConsultation={isGroupConsultation} />,
      'error',
    );
  }

  const rawTranscriptionText = data.transcription.trim();
  const chunks = parseTranscript(rawTranscriptionText);

  if (!chunks.length) {
    return renderCard(
      <div className="space-y-4">
        <div className="rounded-control border border-line bg-surface-muted px-4 py-3 whitespace-pre-line text-ink text-ui lg:text-body-l leading-relaxed">
          {rawTranscriptionText}
        </div>
        <AiAssistantDisclaimer assistant="ai-transcription" />
      </div>,
    );
  }

  const transcriptText = chunks.map((chunk) => `Спикер ${chunk.speaker}: ${chunk.text}`).join('\n\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(transcriptText);
      toast.success('Транскрипт скопирован в буфер');
    } catch (error) {
      toast.error('Не удалось скопировать транскрипт');
    }
  };

  const handleSavePdf = () => {
    const logoUrl = `${window.location.origin}/main-logo-b.svg`;
    const sessionDate = consultation?.date
      ? new Date(consultation.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';
    const html = `
      <!doctype html>
      <html lang="ru">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Транскрипт сессии</title>
        <style>
          body { font-family: Arial, sans-serif; color: #202020; margin: 24px; line-height: 1.6; }
          .header { margin-bottom: 16px; }
          .logo { width: 140px; height: auto; }
          h1 { margin: 12px 0 6px 0; font-size: 24px; }
          .date { color: #6b7280; font-size: 14px; margin-bottom: 16px; }
          .item { margin-bottom: 14px; }
          .speaker { font-weight: 700; }
        </style>
      </head>
      <body>
        <div class="header">
          <img class="logo" src="${logoUrl}" alt="ZiNGiZi" />
        </div>
        <h1>Транскрипт сессии</h1>
        ${sessionDate ? `<div class="date">Дата сессии: ${sessionDate}</div>` : ''}
        ${chunks
          .map(
            (chunk) =>
              `<div class="item"><span class="speaker">Спикер ${chunk.speaker}:</span> ${chunk.text.replace(/\n/g, '<br/>')}</div>`,
          )
          .join('')}
      </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    document.body.appendChild(iframe);

    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      document.body.removeChild(iframe);
      toast.error('Не удалось открыть окно печати');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };

  return renderCard(
    <div className="space-y-5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="text-ui-xs lg:text-ui text-ink-muted">
            Транскрипт сессии готов к скачиванию. Файл будет доступен {retentionHoursText} ч, после чего будет
            автоматически удален.
          </div>
          <div className="flex items-center gap-2">
            <Button size="md" variant="ghostAccent" startIcon={<PrintSmallIcon />} onClick={handleSavePdf}>
              Сохранить
            </Button>
            <Button size="md" variant="ghostAccent" startIcon={<CopySmallIcon />} onClick={handleCopy}>
              Копировать
            </Button>
          </div>
        </div>
        <div className="h-px w-full bg-line" />
      </div>
      {chunks.map((chunk, index) => {
        const orderIndex = speakerOrder.indexOf(chunk.speaker);
        const isRight = orderIndex % 2 === 0;

        return (
          <div
            key={`${chunk.speaker}-${index}`}
            className={`flex items-start gap-4 ${isRight ? 'justify-end' : 'justify-start'}`}
          >
            {!isRight && <div className="min-w-[84px] pt-2 text-ui-strong text-ink">Спикер {chunk.speaker}</div>}
            <div
              className={`relative max-w-[80%] rounded-control border border-line bg-surface-muted px-4 py-3 whitespace-pre-line text-ink text-ui lg:text-body-l leading-relaxed ${
                isRight ? 'mr-1' : 'ml-1'
              }`}
            >
              <span
                className={`absolute top-4 h-3 w-3 rotate-45 border border-line bg-surface-muted ${
                  isRight ? '-right-[7px] border-l-0 border-b-0' : '-left-[7px] border-r-0 border-t-0'
                }`}
              />
              {chunk.text}
            </div>
            {isRight && (
              <div className="min-w-[84px] pt-2 text-ui-strong text-ink text-right">Спикер {chunk.speaker}</div>
            )}
          </div>
        );
      })}
      <AiAssistantDisclaimer assistant="ai-transcription" />
    </div>,
  );
};
